// Small helper components used by TherapeuticsDraftingPanel.
// - CsrTableImport: paste CSV/TSV rows → markdown table injected into a target field
// - PsurSignalChart: monthly FAERS bar chart via /therapeutics/faers/timeline
// - ProtocolSchemaDiagram: SVG study-schema visualizer (screening → randomization → treatment → follow-up)
import React, { useEffect, useState } from "react";
import { api, logChuck } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, TrendingUp, LayoutTemplate } from "lucide-react";

// ---------- CSV / TSV → markdown --------------------------------------------
export function csvToMarkdown(text) {
  const raw = (text || "").trim();
  if (!raw) return "";
  const delim = raw.includes("\t") && !raw.split("\n")[0].includes(",") ? "\t" : ",";
  const rows = raw.split(/\r?\n/).map((r) => splitRow(r, delim));
  if (rows.length === 0) return "";
  const cols = Math.max(...rows.map((r) => r.length));
  const padded = rows.map((r) => {
    const out = r.slice();
    while (out.length < cols) out.push("");
    return out;
  });
  const header = padded[0].map((c) => c.trim() || "—");
  const body = padded.slice(1);
  let md = "| " + header.join(" | ") + " |\n";
  md += "|" + header.map(() => "---").join("|") + "|\n";
  body.forEach((r) => {
    md += "| " + r.map((c) => c.trim().replace(/\|/g, "\\|")).join(" | ") + " |\n";
  });
  return md;
}

function splitRow(row, delim) {
  // Simple CSV parser respecting quoted fields
  if (delim === "\t") return row.split("\t");
  const out = [];
  let cur = "", inQ = false;
  for (let i = 0; i < row.length; i++) {
    const c = row[i];
    if (c === '"' && row[i + 1] === '"') { cur += '"'; i++; continue; }
    if (c === '"') { inQ = !inQ; continue; }
    if (c === delim && !inQ) { out.push(cur); cur = ""; continue; }
    cur += c;
  }
  out.push(cur);
  return out;
}

// ---------- CSR paste-table widget ------------------------------------------
export function CsrTableImport({ onInject, docType, testidBase }) {
  const [efficacyRaw, setEfficacyRaw] = useState("");
  const [safetyRaw, setSafetyRaw] = useState("");
  const [efficacyLabel, setEfficacyLabel] = useState("Primary Endpoint Results");
  const [safetyLabel, setSafetyLabel] = useState("Treatment-Emergent Adverse Events (Safety Population)");

  const inject = (kind) => {
    const raw = kind === "efficacy" ? efficacyRaw : safetyRaw;
    const label = kind === "efficacy" ? efficacyLabel : safetyLabel;
    const md = csvToMarkdown(raw);
    if (!md) { toast.error("Paste at least one row of tabular data"); return; }
    const targetField = kind === "efficacy" ? "efficacy_results" : "safety_results";
    const injected = `### ${label}\n\n${md}\n`;
    onInject(targetField, injected);
    toast.success(`${kind === "efficacy" ? "Efficacy" : "Safety"} table injected`);
    logChuck("table_imported", {
      doc_type: docType,
      payload: { kind, rows: raw.split(/\r?\n/).length, chars: raw.length },
    });
  };

  return (
    <div data-testid={`${testidBase}-table-import`} className="mt-2 border border-dashed border-[#2563EB]/40 bg-[#2563EB]/5 p-3">
      <div className="mb-2 flex items-center gap-2 text-[11px]">
        <FileSpreadsheet className="h-3.5 w-3.5 text-[#2563EB]" />
        <span className="overline text-[#2563EB]">PASTE TABLE FROM EXCEL / CSV — REAL NUMBERS BEAT PROSE</span>
      </div>
      <p className="mb-3 text-[11px] text-slate-600">
        Copy a range from Excel or a CSV and paste below. First row = column headers.
        Injected as a markdown table into Efficacy or Safety results.
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <input
              data-testid={`${testidBase}-efficacy-label`}
              value={efficacyLabel}
              onChange={(e) => setEfficacyLabel(e.target.value)}
              className="flex-1 border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-700"
              placeholder="Table label"
            />
          </div>
          <textarea
            data-testid={`${testidBase}-efficacy-paste`}
            value={efficacyRaw}
            onChange={(e) => setEfficacyRaw(e.target.value)}
            rows={5}
            placeholder={"Arm,N,Mean,95% CI,p-value\nDrug X 50mg,120,-8.4,-10.1 to -6.7,<0.001\nPlacebo,118,-1.2,-2.8 to 0.5,—"}
            className="w-full border border-slate-200 bg-white px-2 py-1.5 font-mono text-[11px] text-slate-800"
          />
          <button
            data-testid={`${testidBase}-inject-efficacy`}
            onClick={() => inject("efficacy")}
            className="mt-1 w-full border border-[#2563EB] bg-[#2563EB] px-2 py-1 text-[11px] font-semibold text-white hover:bg-[#1D4ED8]"
          >
            Inject → Efficacy results
          </button>
        </div>
        <div>
          <div className="mb-1 flex items-center gap-2">
            <input
              data-testid={`${testidBase}-safety-label`}
              value={safetyLabel}
              onChange={(e) => setSafetyLabel(e.target.value)}
              className="flex-1 border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-700"
              placeholder="Table label"
            />
          </div>
          <textarea
            data-testid={`${testidBase}-safety-paste`}
            value={safetyRaw}
            onChange={(e) => setSafetyRaw(e.target.value)}
            rows={5}
            placeholder={"System Organ Class,Drug X n(%),Placebo n(%)\nHeadache,18 (15.0),9 (7.6)\nNausea,12 (10.0),4 (3.4)"}
            className="w-full border border-slate-200 bg-white px-2 py-1.5 font-mono text-[11px] text-slate-800"
          />
          <button
            data-testid={`${testidBase}-inject-safety`}
            onClick={() => inject("safety")}
            className="mt-1 w-full border border-[#2563EB] bg-[#2563EB] px-2 py-1 text-[11px] font-semibold text-white hover:bg-[#1D4ED8]"
          >
            Inject → Safety results
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- PSUR Signal Chart -----------------------------------------------
export function PsurSignalChart({ activeIngredient, start, end, docType }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [serious, setSerious] = useState(0);

  useEffect(() => {
    if (!activeIngredient || !start || !end) return;
    let alive = true;
    setLoading(true);
    api.get("/therapeutics/faers/timeline", { params: { q: activeIngredient, start, end } })
      .then(({ data }) => {
        if (!alive) return;
        setRows(data.buckets || []);
        setTotal(data.total || 0);
        setSerious(data.serious || 0);
      })
      .catch(() => alive && setRows([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [activeIngredient, start, end]);

  if (!activeIngredient || !start || !end) return null;
  if (loading) {
    return (
      <div data-testid="psur-signal-loading" className="flex items-center gap-2 border border-slate-200 bg-white p-3 text-xs text-slate-500">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading FAERS signal chart…
      </div>
    );
  }
  if (!rows) return null;
  if (rows.length === 0) {
    return (
      <div className="border border-slate-200 bg-white p-3 text-xs text-slate-500">
        No FAERS data in the reporting period.
      </div>
    );
  }
  const maxTotal = Math.max(1, ...rows.map((r) => r.total));

  return (
    <div data-testid="psur-signal-chart" className="border border-slate-200 bg-white p-4">
      <div className="mb-2 flex items-center gap-2">
        <TrendingUp className="h-3.5 w-3.5 text-[#2563EB]" />
        <span className="overline text-[#2563EB]">FAERS SIGNAL · {activeIngredient.toUpperCase()} · {total.toLocaleString()} events · {serious.toLocaleString()} serious</span>
      </div>
      <div className="flex h-32 items-end gap-1">
        {rows.map((r) => {
          const totalH = Math.max(2, Math.round((r.total / maxTotal) * 100));
          const seriousH = Math.max(0, Math.round((r.serious / maxTotal) * 100));
          return (
            <div key={r.month} className="group relative flex-1">
              <div className="relative w-full bg-[#2563EB]/30" style={{ height: `${totalH}%` }}>
                <div className="absolute inset-x-0 bottom-0 bg-red-500/80" style={{ height: `${(seriousH / totalH) * 100}%` }} />
              </div>
              <div className="pointer-events-none absolute -top-14 left-1/2 z-10 hidden -translate-x-1/2 whitespace-nowrap border border-slate-200 bg-white px-2 py-1 text-[10px] font-mono text-slate-700 shadow-sm group-hover:block">
                <div><b>{r.month}</b> · {r.total} events</div>
                <div className="text-red-600">{r.serious} serious</div>
                {r.top_reactions?.slice(0, 3).map((tr, i) => (
                  <div key={i} className="text-slate-500">{tr.reaction} × {tr.count}</div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between text-[10px] font-mono text-slate-400">
        <span>{rows[0]?.month}</span>
        <span className="text-slate-500">total (blue) · serious (red)</span>
        <span>{rows[rows.length - 1]?.month}</span>
      </div>
    </div>
  );
}

// ---------- Protocol Schema Diagram -----------------------------------------
export function schemaAscii({ duration_weeks, phase, treatment_arms, n_subjects }) {
  const dur = duration_weeks ? `Week 1 – Week ${duration_weeks}` : "Week 1 – TBD";
  const arms = (treatment_arms || "").split(/\bvs\.?\b|\bvs\b|,|;|\n/).map((s) => s.trim()).filter(Boolean);
  const armsStr = arms.length ? arms.map((a, i) => `  Arm ${i + 1}: ${a}`).join("\n") : "  Arm(s): [SPONSOR TO PROVIDE]";
  return `SCHEMA (${phase || "Phase"}${n_subjects ? " · N=" + n_subjects : ""})
Screening (Day -28 to Day -1)
        ↓
Randomization / Day 1
        ↓
Treatment (${dur})
${armsStr}
        ↓
Follow-up (End-of-Treatment + 4 weeks)
        ↓
End of Study`;
}

export function ProtocolSchemaDiagram({ form }) {
  const arms = (form.treatment_arms || "").split(/\bvs\.?\b|,|;|\n/).map((s) => s.trim()).filter(Boolean);
  const dur = form.duration_weeks || null;
  const phase = form.phase || "Phase";
  const n = form.n_subjects || null;

  return (
    <div data-testid="protocol-schema-diagram" className="border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <LayoutTemplate className="h-3.5 w-3.5 text-[#2563EB]" />
        <span className="overline text-[#2563EB]">STUDY SCHEMA · {phase}{n ? ` · N=${n}` : ""}</span>
      </div>
      <svg viewBox="0 0 720 240" className="w-full text-slate-800">
        {/* Stages */}
        {[
          { x: 20,  label: "Screening",      sub: "Day -28 → Day -1" },
          { x: 180, label: "Randomization",  sub: "Day 1" },
          { x: 340, label: "Treatment",      sub: dur ? `Week 1 – ${dur}` : "Week 1 – TBD" },
          { x: 500, label: "Follow-up",      sub: "EOT + 4 weeks" },
          { x: 630, label: "End of Study",   sub: "Database lock" },
        ].map((s, i, arr) => (
          <g key={s.label}>
            <rect x={s.x} y={80} width={i === 4 ? 80 : 140} height={44}
                  className="fill-[#2563EB]/5 stroke-[#2563EB]" strokeWidth="1.2" />
            <text x={s.x + (i === 4 ? 40 : 70)} y={100} textAnchor="middle"
                  className="fill-slate-900" fontSize="12" fontWeight="600">{s.label}</text>
            <text x={s.x + (i === 4 ? 40 : 70)} y={116} textAnchor="middle"
                  className="fill-slate-500" fontSize="10" fontFamily="monospace">{s.sub}</text>
            {i < arr.length - 1 && (
              <g>
                <line x1={s.x + (i === 4 ? 80 : 140)} y1={102} x2={arr[i + 1].x} y2={102}
                      className="stroke-slate-400" strokeWidth="1.2" />
                <polygon points={`${arr[i + 1].x - 6},98 ${arr[i + 1].x},102 ${arr[i + 1].x - 6},106`}
                         className="fill-slate-400" />
              </g>
            )}
          </g>
        ))}
        {/* Arms lane under Treatment */}
        <text x={410} y={155} textAnchor="middle" fontSize="10" className="fill-slate-500">Treatment arms:</text>
        {(arms.length ? arms : ["[SPONSOR TO PROVIDE]"]).slice(0, 4).map((a, i) => (
          <g key={i}>
            <rect x={340} y={165 + i * 18} width={140} height={14}
                  className={`fill-white stroke-slate-300`} strokeWidth="1" />
            <text x={410} y={175 + i * 18} textAnchor="middle" fontSize="10" className="fill-slate-700">
              {a.length > 30 ? a.slice(0, 27) + "…" : a}
            </text>
          </g>
        ))}
      </svg>
      <div className="mt-2 text-[10px] font-mono text-slate-400">
        Auto-generated from form fields · injected into the AI prompt so the draft references it
      </div>
    </div>
  );
}
